/* ═══════════════════════════════════════════════════════════════════
   [D-414] Список прав на функції — спільний для картки юзера і картки
   партнера.

   🔴 Навмисно ОДИН компонент на два екрани. Верстка тут не проста
   (три стани, два різні сенси «вимкнено», кольорові індикатори), і дві
   копії неминуче розійшлися б — як уже розходився рецепт унікалізації.

   Вимога OWNER (28.09): «щоб не перечитуючи було одразу видно на око,
   які функції працюють, а які ні». Тому стан несе КОЛІР і великий
   підпис, а не дрібний сірий текст збоку.
   ═══════════════════════════════════════════════════════════════════ */
import React from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import api from '../api';   // [D-495] прибрати позначку «🆕»
import { helpFor } from './featureHelp';   // [D-528] «ℹ️ Як це працює»
import { VerChips, VerLegend, verOfArea, verText } from './editorVer';   // [D-697] до якого редактора (v-1…v-5)

// [D-495] OWNER 08.10: «мені в дашборді поміть ці функції якось окремо, можливо фоном щоб я знав де їх вмикати коли я вирішу партнеру».
// Позначка живе в довіднику (feature_registry.badge): картка — жовтим фоном, на кнопці розділу — «🆕 N».
const NEW_BG = 'rgba(255,193,7,.13)', NEW_LINE = '#ffc107';

const C = {
  on:    '#00e676',   // працює
  off:   '#ffa726',   // кнопка є, але стоїть вимкненою
  shut:  '#ff5252',   // закрито: кнопки немає
  sil:   '#29b6f6',   // [D-540] працює приховано: меню немає, партнер не бачить, а в учнів діє
  txt:   '#eaf6ff',
  dim:   '#8fa3b8',
  neon:  '#7ffcd3',
};

// [D-468] OWNER 06.10: «треба зробити (Показувати/сховати)». У партнера (twoState) «Як усім» немає зовсім:
// кожна функція задана явно, нова — одразу сховано. У картці юзера третій стан лишився — там він означає
// «як у партнера / як усім» і без нього не повернути юзера до загального.
const STATES = [
  ['inherit', 'Як усім', 'var(--ac1, #7c4dff)'],
  ['open', '👁 Показувати', '#4caf50'],
  ['closed', '🙈 Сховати', '#f44336'],
  // [D-540] OWNER 12.10: «функція… повинна працювати у нього та його користувачів по дефолту, але я не хочу щоб він бачив меню цієї
  // функції і взагалі що вона у нього є». Лише в картці партнера і лише на функціях, які вміють діяти самі (f.can_silent).
  ['silent', '⚙️ Без меню', '#29b6f6'],   // [D-541] було «Приховано» — перейменовано, щоб не плутати з «🕶 сховати картку від партнера»
];
const isSil = (f) => f.own === 'silent' || !!f.sil;
// [D-541] OWNER 12.10: «не зупинити цю функцію і не заборонити а саме сховати її у списку партнера щоб він її не бачив».
// Картка функції, яку власник сховав від партнера в його кабінеті; функція при цьому показана учням і працює.
const isCab = (f) => !!f.cab_hidden && !!f.eff_vis;
const CAB = '#b388ff';

// 🔴 «Вимкнено» означає різне в різних розділах.
// У «Посиленні» кнопка — це перемикач: вимкнена, але юзер може ввімкнути.
// У меню бота кнопка — це вхід кудись: вона або є, або її немає, і
// «вимкнено» там не має сенсу взагалі.
function stateOf(f) {
  if (isSil(f)) return { c: C.sil, t: 'ПРАЦЮЄ БЕЗ МЕНЮ', hint: 'в учнів діє сама, меню в них немає; партнер її не бачить' };
  if (!f.eff_vis) return { c: C.shut, t: 'СХОВАНО', hint: 'юзер цього не бачить' };
  if (f.area === 'hyper' && !f.eff_val) {
    return { c: C.off, t: 'ВИМКНЕНО', hint: 'кнопка є, стоїть вимкненою — юзер може ввімкнути' };
  }
  return { c: C.on, t: 'ПРАЦЮЄ', hint: '' };
}

/* [D-536] OWNER 11.10: «створити кнопку пошука по дозволеним і не дозволеним показам функцій. Щоб я міг швидко знайти функцію яка
   ввімкнена для партнера і заборонити показ її йому. І так само… знайти і натиснути показати партнеру».
   Пошук — по назві, опису й розділу, одразу по всіх розділах. Пробачає одруківку («обкладенка») і кирилицю замість латинки («АІ»):
   схожі літери зводимо до одних, а для слів від 5 літер дозволяємо 1 неточну літеру (від 9 — дві). Точні збіги стоять першими. */
const LOOK = { a: 'а', e: 'е', i: 'і', o: 'о', c: 'с', p: 'р', x: 'х', y: 'у', k: 'к', m: 'м', t: 'т', 'ё': 'е' };
const norm = (s) => String(s || '').toLowerCase().replace(/[ʼ'’`]/g, '').replace(/[aeiocpxykmtё]/g, ch => LOOK[ch]);
const toks = (s) => norm(s).split(/[^a-zа-яіїєґ0-9]+/).filter(Boolean);
// найменша кількість правок, щоб слово `w` стало ПОЧАТКОМ слова `t`
function prefixDist(w, t) {
  let prev = [], best;
  for (let j = 0; j <= t.length; j++) prev[j] = j;
  for (let i = 1; i <= w.length; i++) {
    const cur = [i];
    for (let j = 1; j <= t.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (w[i - 1] === t[j - 1] ? 0 : 1));
    prev = cur;
  }
  best = prev[0];
  for (let j = 1; j <= t.length; j++) if (prev[j] < best) best = prev[j];
  return best;
}
// 0 — усі слова є в назві; 1 — у назві з одруківкою; 2 — в описі чи назві розділу; 3 — там само з одруківкою; -1 — не підходить
export function featMatch(f, q) {
  const ws = toks(q);
  if (!ws.length) return 0;
  const nq = norm(q).trim();
  if (nq.length >= 4 && norm(f.key).includes(nq)) return 0;   // технічна назва (v5_cover)
  const title = norm(f.title), hay = norm([f.descr, f.area_title, helpFor(f).what].join(' '));
  const tT = toks(title), hT = toks(hay);
  let rank = 0;
  for (const w of ws) {
    // коротке слово (до 3 літер) — лише з початку слова: «фон» не має знаходити «телефон», «АІ» — «заінтригувати»
    const has = (str, list) => (w.length <= 3 ? list.some(t => t.startsWith(w)) : str.includes(w));
    const lim = w.length >= 9 ? 2 : w.length >= 5 ? 1 : 0;
    const near = (list) => lim > 0 && list.some(t => prefixDist(w, t) <= lim);
    if (has(title, tT)) continue;
    if (near(tT)) { rank = Math.max(rank, 1); continue; }
    if (has(hay, hT)) { rank = Math.max(rank, 2); continue; }
    if (!near(hT)) return -1;
    rank = 3;
  }
  return rank;
}

export function FeatureLegend() {
  return (<>
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.2 }}>
      {[[C.on, 'ПРАЦЮЄ'], [C.off, 'ВИМКНЕНО'], [C.shut, 'СХОВАНО'], [C.sil, 'ПРАЦЮЄ БЕЗ МЕНЮ']].map(([c, t]) => (
        <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
          <Typography sx={{ fontSize: 11, color: C.dim }}>{t}</Typography>
        </Box>
      ))}
    </Box>
    <VerLegend sx={{ mb: 1.2 }} />{/* [D-697] */}
  </>);
}

// [D-447] `onSetArea` — необовʼязковий: «на весь розділ» є лише там, де сторінка його передала (картка партнера).
// `ownLabel` — як підписати власне налаштування: у юзера «особисто», у партнера «для партнера».
// [D-459] OWNER 06.10: «ніколи, ніде не роби списками вниз». Розділи — кнопками (на кнопці видно, скільки
// закрито), на екрані лише вибраний розділ, функції — картками в сітці.
export default function FeatureRows({ rows, onSet, busyKey, onSetArea, ownLabel = 'особисто', twoState = false, inheritLabel = 'Як усім', onPreset, onPick, picked, onCab }) {
  const [help, setHelp] = React.useState({});   // [D-528] розгорнуті інструкції
  const ST0 = STATES.filter(x => x[0] !== 'silent');   // масові кнопки й картка юзера — без «приховано»
  const ST = twoState ? ST0.filter(x => x[0] !== 'inherit') : ST0.map(x => (x[0] === 'inherit' ? [x[0], inheritLabel, x[2]] : x));
  const stOf = (f) => (twoState && f.can_silent ? [...ST, STATES[3]] : ST);   // [D-540] третя кнопка — лише де функція вміє діяти сама
  // групуємо за розділом, а не «підряд» (рядки йдуть за sort_order довідника)
  const groups = [], gi = {};
  (rows || []).forEach(f => {
    const t = f.area_title || '';
    if (gi[t] === undefined) { gi[t] = groups.length; groups.push({ t, area: f.area, rows: [] }); }
    groups[gi[t]].rows.push(f);
  });
  const [sel, setSel] = React.useState(null);
  const [cleared, setCleared] = React.useState({});   // [D-495] позначки, прибрані щойно (без перезавантаження)
  const badgeOf = (f) => (f.badge && !cleared[f.key] ? f.badge : null);
  const newN = (x) => x.rows.filter(badgeOf).length;
  const allNew = (rows || []).filter(badgeOf);
  const clearBadge = (key) => api.post(`/features/${encodeURIComponent(key)}/badge`, { badge: '' })
    .then(() => setCleared(c => ({ ...c, [key]: true }))).catch(() => {});
  // [D-536] пошук по всіх розділах одразу
  const [q, setQ] = React.useState('');
  const [flt, setFlt] = React.useState('all');     // all | shown | shut
  const [stick, setStick] = React.useState({});    // щойно змінені картки лишаються на екрані, доки не зміниться запит
  // [D-538] OWNER 11.10: «Назву функції відкритої для партнера треба перед відкриттям щоб я вводив як ця функція буде називатися у
  // нього, бо я не хочу щоб він бачив оригінальні назви». Діє в картці партнера (twoState): назви — спільний список «🏷 Назви».
  const [alias, setAlias] = React.useState(null);
  React.useEffect(() => { if (twoState) api.get('/feature-aliases').then(r => setAlias((r.data && r.data.aliases) || {})).catch(() => setAlias({})); }, [twoState]);
  const askAlias = (f, must) => {
    const cur = (alias && alias[f.key]) || '';
    const v = window.prompt((must ? 'Перш ніж показати партнеру — як ця функція називатиметься В НЬОГО?\n' : 'Назва цієї функції в партнера:\n') + '\nОригінал (бачите лише ви): ' + f.title
      + (must ? '\n\nПартнер побачить саме те, що ви впишете. «Скасувати» — нічого не відкривати.' : '\n\nПорожньо — партнер бачитиме оригінальну назву.'), cur);
    if (v === null) return Promise.resolve(null);
    const name = v.trim().slice(0, 60);
    if (must && !name) return Promise.resolve(null);
    return api.put('/feature-aliases', { key: f.key, name }).then(r => { setAlias((r.data && r.data.aliases) || {}); return name; }).catch(() => null);
  };
  const g = groups.find(x => x.t === sel) || groups[0];
  if (!g) return null;
  const shutN = (x) => x.rows.filter(f => !f.eff_vis && !isSil(f)).length;
  const silN = (x) => x.rows.filter(isSil).length;   // [D-540]
  const cabN = (x) => x.rows.filter(isCab).length;   // [D-541]
  const searching = !!q.trim() || flt !== 'all';
  const byQ = (rows || []).map((f, i) => ({ f, i, r: featMatch(f, q) })).filter(x => x.r >= 0).sort((a, b) => a.r - b.r || a.i - b.i);
  const found = byQ.filter(x => flt === 'all' || stick[x.f.key] || (flt === 'shown' ? x.f.eff_vis : flt === 'sil' ? isSil(x.f) : flt === 'cab' ? isCab(x.f) : (!x.f.eff_vis && !isSil(x.f))));
  const nShown = byQ.filter(x => x.f.eff_vis).length;
  const nSil = byQ.filter(x => isSil(x.f)).length;   // [D-540]
  const nCab = byQ.filter(x => isCab(x.f)).length;   // [D-541]
  const FLT = [['all', 'Усі', byQ.length, '#00e5a0'], ['shown', '👁 Показано', nShown, '#4caf50'], ['shut', '🙈 Сховано', byQ.length - nShown - nSil, '#f44336'],
               ...(twoState || nSil ? [['sil', '⚙️ Без меню', nSil, '#29b6f6']] : []),
               ...(onCab ? [['cab', '🕶 Сховано від партнера', nCab, '#b388ff']] : [])];   // [D-541]
  const ask = (v) => { setQ(v); setStick({}); };
  const pickFlt = (v) => { setFlt(v); setStick({}); };
  const leave = (t) => { setQ(''); setFlt('all'); setStick({}); if (t !== undefined) setSel(t); };
  const doSet = (key, s2) => {
    const go = () => { if (searching) setStick(s => ({ ...s, [key]: true })); onSet(key, s2); };
    const f = (rows || []).find(x => x.key === key);
    const named = /^(st_|app_)/.test(key) || key === 'hub_apps' || key === 'btn_hub';   // партнер цих карток не бачить — назва не потрібна
    if (twoState && s2 === 'open' && f && alias && !alias[key] && !named && !f.cab_hidden) { askAlias(f, true).then(nm => { if (nm) go(); }); return; }
    go();
  };

  const card = (f, withArea) => {
    const st = stateOf(f);
    return (
      <Box key={f.key} data-fk={f.key} sx={{ p: 1.2, borderRadius: 2, bgcolor: badgeOf(f) ? NEW_BG : 'rgba(255,255,255,.03)', borderTop: `3px solid ${st.c}`,
                             outline: picked === f.key ? '2px solid #ffd166' : badgeOf(f) ? `1px solid ${NEW_LINE}` : 'none',
                             display: 'flex', flexDirection: 'column', gap: .6 }}>
        {withArea && (   /* [D-536] у результатах пошуку — з якого розділу картка; натиск відкриває цей розділ */
          <Button size="small" data-fs="area" onClick={() => leave(f.area_title || '')} title="Відкрити цей розділ цілком"
            sx={{ alignSelf: 'flex-start', fontSize: 10.5, textTransform: 'none', minWidth: 0, px: .7, py: 0, color: C.neon, border: '1px solid rgba(0,229,160,.25)', borderRadius: 1.5 }}>
            📂 {f.area_title || '—'}</Button>
        )}
        {badgeOf(f) && (   /* [D-495] */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: NEW_LINE, flex: 1 }}>{badgeOf(f)}</Typography>
            <Button size="small" onClick={() => clearBadge(f.key)} title="Прибрати позначку з цієї функції (усюди в дашборді)"
              sx={{ fontSize: 10.5, textTransform: 'none', minWidth: 0, px: .7, py: 0, color: C.dim }}>✓ прибрати позначку</Button>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, flexWrap: 'wrap' }}>
          <VerChips k={f.key} area={f.area} size="xs" />{/* [D-697] */}
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: f.eff_vis || isSil(f) ? C.txt : '#7a8a99', flex: 1, minWidth: 0 }}>{f.title}</Typography>
          {f.g15new && (   /* [D-525] додано після узгодженого набору G15 (06.10) */
            <Box title={'Додано ' + String(f.created || '').slice(0, 10)} sx={{ px: .7, py: .1, borderRadius: .8, border: '1px solid #ffd166' }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#ffd166' }}>🆕 після G15</Typography>
            </Box>
          )}
          <Box sx={{ px: .8, py: .1, borderRadius: .8, bgcolor: st.c }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#06120a', letterSpacing: .4 }}>{st.t}</Typography>
          </Box>
        </Box>
        {f.app_warn && (   /* [D-546] продукт власника вимкнений у «📱 Наші APP» або без адреси: право тут нічого не дає, учні його не побачать */
          <Typography data-fs="appwarn" sx={{ fontSize: 11.5, fontWeight: 700, color: '#ff8a8a', mt: .4, lineHeight: 1.4 }}>
            ⚠️ Учні цього продукту НЕ побачать, навіть якщо тут «показано»: {f.app_warn}. Увімкніть продукт (і впишіть адресу) в розділі «📱 Наші APP».</Typography>
        )}
        {twoState && alias && !f.cab_hidden && !/^(st_|app_)/.test(f.key) && f.key !== 'hub_apps' && f.key !== 'btn_hub' && (   /* [D-538] як це називається в партнера */
          <Button size="small" data-fs="alias" onClick={() => askAlias(f, false)} title="Змінити назву, яку бачить партнер"
            sx={{ alignSelf: 'flex-start', fontSize: 10.5, textTransform: 'none', minWidth: 0, px: .7, py: 0, textAlign: 'left', color: alias[f.key] ? '#ffd166' : C.dim, border: `1px dashed ${alias[f.key] ? '#ffd166' : 'rgba(255,255,255,.2)'}`, borderRadius: 1.5 }}>
            🏷 у партнера: {alias[f.key] ? '«' + alias[f.key] + '»' : 'назви ще немає'} ✏️</Button>
        )}
        {twoState ? (   /* [D-468] у партнера «своє» — кожен рядок, тож підпис лише про важливе */
          (f.own === 'inherit' || (f.eff_vis && f.glob_vis === false) || st.hint) && (
            <Typography sx={{ fontSize: 11, color: f.own === 'inherit' || (f.eff_vis && f.glob_vis === false) ? C.off : C.dim }}>
              {f.own === 'inherit' ? 'не задано — діє як у всіх; оберіть стан'
                : (f.eff_vis && f.glob_vis === false) ? 'для всіх сховано — бачать лише учні цього партнера'
                : st.hint}
            </Typography>
          )
        ) : (st.hint || f.own !== 'inherit') && (
          <Typography sx={{ fontSize: 11, color: f.own !== 'inherit' ? '#c9a7ff' : C.dim }}>
            {f.own !== 'inherit' ? ownLabel + (st.hint ? ' · ' : '') : ''}{st.hint}
          </Typography>
        )}
        {onCab && f.eff_vis && !/^(st_|app_)/.test(f.key) && f.key !== 'hub_apps' && f.key !== 'btn_hub' && (
          <Button size="small" data-fs="cab" data-cab={f.cab_hidden ? 1 : 0} onClick={() => { if (searching) setStick(s => ({ ...s, [f.key]: true })); onCab(f.key, !f.cab_hidden); }}
            title={f.cab_hidden ? 'Зараз партнер цієї картки у своєму кабінеті НЕ бачить, а функція працює. Натисніть — повернути картку партнеру.'
              : 'Функція лишиться ввімкненою й працюватиме в учнів, але з кабінету партнера («Функції учням», картка учня) її картка зникне — він її не побачить і не зможе закрити.'}
            sx={{ alignSelf: 'stretch', justifyContent: 'flex-start', textAlign: 'left', fontSize: 11, lineHeight: 1.3, textTransform: 'none', minWidth: 0, px: .8, py: .4, borderRadius: 1.5,
                  border: `1px ${f.cab_hidden ? 'solid' : 'dashed'} ${f.cab_hidden ? CAB : 'rgba(255,255,255,.2)'}`, bgcolor: f.cab_hidden ? 'rgba(179,136,255,.16)' : 'transparent', color: f.cab_hidden ? '#d9c6ff' : C.dim }}>
            {f.cab_hidden ? '🕶 Картку сховано від партнера — функція працює, він її не бачить · повернути' : '🕶 Сховати картку від партнера (функція працюватиме)'}</Button>
        )}
        {twoState && f.own === 'silent' && f.silent_note && (   /* [D-540] що саме відбувається в учнів */
          <Typography data-fs="silnote" sx={{ fontSize: 11, color: '#9fdcff', bgcolor: 'rgba(41,182,246,.10)', borderRadius: 1, px: .7, py: .4, lineHeight: 1.4 }}>⚙️ {f.silent_note}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: .5 }}>
          {stOf(f).map(([s2, lbl, col]) => (
            <Button key={s2} size="small" data-fs-st={s2} disabled={busyKey === f.key}
              variant={f.own === s2 ? 'contained' : 'outlined'} onClick={() => doSet(f.key, s2)}
              sx={{ flex: 1, fontSize: 11, textTransform: 'none', minWidth: 0, px: .5,
                    bgcolor: f.own === s2 ? col : 'transparent', borderColor: col, color: f.own === s2 ? '#fff' : col }}>
              {lbl}
            </Button>
          ))}
        </Box>
        {/* [D-528] OWNER: «у кожен пункт додати розгортаючу інструкцію як це працює і що воно дасть»; «📍» — підсвітити в телефоні */}
        <Box sx={{ display: 'flex', gap: .5 }}>
          <Button size="small" onClick={() => setHelp(h => ({ ...h, [f.key]: !h[f.key] }))}
            sx={{ flex: 1, fontSize: 10.5, textTransform: 'none', minWidth: 0, py: .1, color: help[f.key] ? '#ffd166' : C.dim, justifyContent: 'flex-start' }}>
            ℹ️ Як це працює {help[f.key] ? '▴' : '▾'}</Button>
          {onPick && <Button size="small" onClick={() => onPick(f.key)} title="Телефон праворуч прокрутиться до цієї функції, і її блок заблимає жовтим — видно, де вона в учня"
            sx={{ fontSize: 10.5, textTransform: 'none', minWidth: 0, py: .1, color: C.neon }}>📍 Де це в телефоні</Button>}
        </Box>
        {help[f.key] && (() => {
          const h = helpFor(f);
          return (
            <Box sx={{ fontSize: 11.5, lineHeight: 1.5, color: C.txt, bgcolor: 'rgba(255,255,255,.04)', borderRadius: 1.5, p: .8 }}>
              <div><b style={{ color: '#ffd166' }}>📍 Де:</b> {h.where}</div>
              <div><b style={{ color: C.on }}>✅ Що дає:</b> {h.what}</div>
              <div><b style={{ color: C.shut }}>🙈 Якщо сховати:</b> {h.hide}</div>
              {twoState && <div><b style={{ color: C.sil }}>⚙️ Якщо «Без меню»:</b> {f.can_silent ? f.silent_note + ' Партнер у кабінеті цієї функції не бачить.'
                : 'цій функції такого стану немає — без меню їй нічого виконувати (це інструмент або кнопка).'}</div>}
            </Box>
          );
        })()}
      </Box>
    );
  };

  return (
    <>
      {/* [D-536] пошук функції по всіх розділах + «лише показані / лише сховані» */}
      <Box data-fs="bar" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1.2, p: 1, borderRadius: 2,
                               border: `1px solid ${searching ? '#00e5a0' : 'rgba(0,229,160,.35)'}`, bgcolor: 'rgba(0,229,160,.06)' }}>
        <TextField size="small" value={q} onChange={e => ask(e.target.value.slice(0, 60))} placeholder="🔎 Пошук функції — напр. «обкладинка», «субтитри», «оплата»"
          inputProps={{ 'data-fs': 'q', style: { color: C.txt, fontSize: 13.5 } }}
          sx={{ flex: '1 1 260px', minWidth: 220, '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(0,229,160,.35)' },
                '& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#00e5a0' } }} />
        <Box sx={{ display: 'flex', flex: 'none' }}>
          {FLT.map(([k, lbl, n, col], i) => (
            <Button key={k} size="small" data-fs={'f-' + k} onClick={() => pickFlt(k)}
              sx={{ textTransform: 'none', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', px: 1.1, py: .5,
                    borderRadius: i === 0 ? '8px 0 0 8px' : i === FLT.length - 1 ? '0 8px 8px 0' : 0, border: `1px solid ${col}`, ml: i ? '-1px' : 0,
                    bgcolor: flt === k ? col : 'transparent', color: flt === k ? '#06120a' : col, '&:hover': { bgcolor: flt === k ? col : 'rgba(255,255,255,.06)' } }}>
              {lbl} · {n}</Button>
          ))}
        </Box>
        {searching && <Button size="small" data-fs="x" onClick={() => leave()}
          sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap', color: C.txt, border: '1px solid rgba(255,255,255,.2)', borderRadius: 2 }}>✕ До розділів</Button>}
      </Box>

      {searching ? (<>
        <Typography data-fs="sum" sx={{ fontSize: 13.5, fontWeight: 700, color: C.txt, mt: 1.4, mb: .8 }}>
          🔎 {q.trim() ? <>За словом «{q.trim()}»</> : flt === 'shown' ? 'Усе, що показано' : flt === 'sil' ? 'Усе, що працює без меню' : flt === 'cab' ? 'Картки, сховані від партнера' : 'Усе, що сховано'} — знайдено {found.length}
          {found.length > 0 && <span style={{ color: C.dim, fontWeight: 400, fontSize: 12.5 }}>
            {' '}· показано {found.filter(x => x.f.eff_vis).length} · сховано {found.filter(x => !x.f.eff_vis && !isSil(x.f)).length}
            {found.some(x => isSil(x.f)) ? ' · ⚙️ без меню ' + found.filter(x => isSil(x.f)).length : ''}
            {found.some(x => isCab(x.f)) ? ' · 🕶 картку сховано від партнера ' + found.filter(x => isCab(x.f)).length : ''}
            {found.some(x => x.r === 1 || x.r === 3) ? ' · враховано схожі за написанням' : ''}</span>}
        </Typography>
        {found.length === 0 ? (
          <Typography data-fs="none" sx={{ fontSize: 12.5, color: C.off, mb: 1 }}>
            {byQ.length > 0
              ? <>Такі функції є ({byQ.length}), але всі вони в іншому стані — натисніть «Усі».</>
              : <>Нічого не знайдено. Спробуйте одне коротше слово — пошук іде по назві й опису функції в усіх розділах.</>}
          </Typography>
        ) : (
          [[found.filter(x => x.r <= 1), 'grid'], [found.filter(x => x.r > 1), 'more']].map(([list, k], i, all) => list.length > 0 && (
            <React.Fragment key={k}>
              {k === 'more' && q.trim() && (   /* слово не в назві, а лише в описі — окремо, щоб не плутати з потрібною функцією */
                <Typography data-fs="more-h" sx={{ fontSize: 12, color: C.dim, mt: all[0][0].length ? 1.4 : 0, mb: .8 }}>
                  {all[0][0].length ? 'ℹ️ Ще згадують це в описі' : 'ℹ️ У назвах такого немає — згадують в описі'} ({list.length}):</Typography>
              )}
              <Box data-fs={k} sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))' }}>
                {list.map(x => card(x.f, true))}
              </Box>
            </React.Fragment>
          ))
        )}
      </>) : (<>
      {allNew.length > 0 && (   /* [D-495] де нові функції */
        <Box sx={{ mt: 1.5, p: 1.1, borderRadius: 2, bgcolor: NEW_BG, border: `1px solid ${NEW_LINE}` }}>
          <Typography sx={{ fontSize: 12.5, color: '#ffe08a', fontWeight: 700 }}>
            🆕 Нові функції ({allNew.length}) — позначено жовтим. Партнерам вони поки сховані; коли вирішите — «👁 Показувати» в картці партнера,
            і партнер зможе вмикати їх своїм учням («🧩 Функції для всіх учнів» у його кабінеті).
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: C.dim, mt: .4 }}>{allNew.map(f => f.title).join(' · ')}</Typography>
        </Box>
      )}
      {/* [D-470] кнопки нижче — РОЗДІЛИ бота (не вибір): натиснули — під ними функції цього розділу */}
      <Typography sx={{ fontSize: 12, color: C.dim, mt: 1.5 }}>📂 Розділи бота — натисніть розділ, нижче з'являться його функції. Цифра — скільки функцій у розділі, «🙈» — скільки сховано{twoState ? ', «⚙️» — скільки працює без меню, «🕶» — скільки карток сховано від партнера' : ''}.</Typography>
      <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', mt: .8, mb: 1.5 }}>
        {groups.map(x => {
          const on = x === g, n = shutN(x);
          return (
            <Button key={x.t} size="small" onClick={() => setSel(x.t)} variant={on ? 'contained' : 'outlined'}
              sx={{ textTransform: 'none', justifyContent: 'space-between', alignItems: 'center', gap: 1, borderRadius: 2,
                    fontSize: 12.5, px: 1.2, py: .6, lineHeight: 1.25, textAlign: 'left',
                    borderColor: 'rgba(0,229,160,.25)', color: on ? '#04121a' : C.neon, bgcolor: on ? '#00e5a0' : 'transparent',
                    '&:hover': { bgcolor: on ? '#00e5a0' : 'rgba(0,229,160,.08)' } }}>
              <span>{x.t || '—'}{verText(verOfArea(x.area, x.rows.map(r => r.key))) && <b data-fs="grp-ver" style={{ marginLeft: 6, fontSize: 10.5, opacity: .85 }}>{verText(verOfArea(x.area, x.rows.map(r => r.key)))}</b>}{/* [D-697] */}
                {newN(x) > 0 && <b style={{ marginLeft: 6, color: on ? '#04121a' : NEW_LINE }}>🆕 {newN(x)}</b>}
                {silN(x) > 0 && <b data-fs="grp-sil" style={{ marginLeft: 6, color: on ? '#04121a' : C.sil }}>⚙️ {silN(x)}</b>}
                {onCab && cabN(x) > 0 && <b data-fs="grp-cab" style={{ marginLeft: 6, color: on ? '#04121a' : CAB }}>🕶 {cabN(x)}</b>}</span>
              <span title={n ? 'сховано ' + n + ' з ' + x.rows.length : 'усього ' + x.rows.length}
                style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 10.5, fontWeight: 800, borderRadius: 8, padding: '1px 6px',
                         background: n ? (on ? 'rgba(4,18,26,.18)' : 'rgba(255,82,82,.15)') : 'transparent',
                         color: on ? '#04121a' : (n ? C.shut : C.dim) }}>
                {n ? (n === x.rows.length ? '🙈 усі' : '🙈 ' + n) : x.rows.length}
              </span>
            </Button>
          );
        })}
      </Box>

      {onPreset && (   /* [D-470] узгоджений набір */
        <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', flexWrap: 'wrap', mb: .8 }}>
          <Typography sx={{ fontSize: 12, color: C.dim }}>Узгоджений набір (виставляється сам при створенні партнера):</Typography>
          <Button size="small" variant="outlined" onClick={onPreset}
            sx={{ fontSize: 11.5, textTransform: 'none', minWidth: 0, px: 1, py: .1, borderColor: C.neon, color: C.neon }}>🧬 Повернути набір G15</Button>
        </Box>
      )}
      {onSetArea && twoState && (   /* [D-468] одним кліком на весь бот */
        <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', flexWrap: 'wrap', mb: .8 }}>
          <Typography sx={{ fontSize: 12, color: C.dim }}>Увесь бот ({(rows || []).filter(f => f.eff_vis).length} з {(rows || []).length} показано):</Typography>
          {[['asall', '📋 Показати те, що бачать усі', '#7c4dff'], ['open', '👁 Показати все', '#4caf50'], ['closed', '🙈 Сховати все', '#f44336']].map(([st, lbl, col]) => (
            <Button key={st} size="small" variant="outlined" onClick={() => onSetArea('*', st, 'весь бот')}
              sx={{ fontSize: 11.5, textTransform: 'none', minWidth: 0, px: 1, py: .1, borderColor: col, color: col }}>{lbl}</Button>
          ))}
        </Box>
      )}
      {onSetArea && g.area && (
        <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', flexWrap: 'wrap', mb: 1.2 }}>
          <Typography sx={{ fontSize: 12, color: C.dim }}>Увесь розділ «{g.t}»:</Typography>
          {ST.map(([st, lbl, col]) => (
            <Button key={st} size="small" variant="outlined" onClick={() => onSetArea(g.area, st, g.t)}
              sx={{ fontSize: 11.5, textTransform: 'none', minWidth: 0, px: 1, py: .1, borderColor: col, color: col }}>{lbl}</Button>
          ))}
        </Box>
      )}

      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.txt, mb: .8 }}>   {/* [D-470] */}
        📂 Розділ «{g.t}» — показано {g.rows.filter(f => f.eff_vis).length} з {g.rows.length}{silN(g) > 0 ? ' · ⚙️ без меню ' + silN(g) : ''}{onCab && cabN(g) > 0 ? ' · 🕶 картку сховано від партнера ' + cabN(g) : ''}
      </Typography>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))' }}>
        {g.rows.map(f => card(f, false))}
      </Box>
      </>)}
    </>
  );
}
